export type ViewportCallback = (event: Event) => void;
export type ViewportUnsubscribeCallback = () => void;

export class ViewportChangeListener {
  private readonly windowEvents: string[] = ['resize', 'orientationchange'];
  private readonly documentEvents: string[] = [
    'fullscreenchange',
    'mozfullscreenchange',
    'msfullscreenchange',
    'webkitfullscreenchange',
  ];

  private callbacks = new Set<ViewportCallback>();
  private listening = false;
  private readonly handler = (e: Event) => this.dispatch(e);

  public on(cb: ViewportCallback): ViewportUnsubscribeCallback {
    if (!this.callbacks.has(cb)) {
      this.callbacks.add(cb);
    }
    this.ensureListening();
    return () => this.off(cb);
  }

  public off(callback?: ViewportCallback): void {
    if (callback) {
      this.callbacks.delete(callback);
    } else {
      this.callbacks.clear();
    }
    if (this.callbacks.size === 0) {
      this.stopListening();
    }
  }

  public trigger(): void {
    this.dispatch(new Event('manual'));
  }

  public dispose(): void {
    this.callbacks.clear();
    this.stopListening();
  }

  private dispatch(e: Event): void {
    if (this.callbacks.size === 0) {
      this.stopListening();
      return;
    }

    Array.from(this.callbacks).forEach((callback) => {
      callback(e);
    });
  }

  private ensureListening(): void {
    if (this.listening || !this.isDomAvailable()) {
      return;
    }
    this.windowEvents.forEach((event) => {
      window.addEventListener(event, this.handler, { passive: true });
    });
    this.documentEvents.forEach((event) => {
      document.addEventListener(event, this.handler, { passive: true });
    });
    this.listening = true;
  }

  private stopListening(): void {
    if (!this.listening || !this.isDomAvailable()) {
      return;
    }
    this.windowEvents.forEach((event) => {
      window.removeEventListener(event, this.handler);
    });
    this.documentEvents.forEach((event) => {
      document.removeEventListener(event, this.handler);
    });
    this.listening = false;
  }

  private isDomAvailable(): boolean {
    return typeof window !== 'undefined' && typeof document !== 'undefined';
  }
}

export const viewportChangeListener = new ViewportChangeListener();
